import React from "react"
import { Calendar, CheckCircle, Mail, Shield, Users, Zap } from "lucide-react"
import type { FeatureItem } from "@/lib/types"

const features: FeatureItem[] = [
  {
    icon: Mail,
    title: "Reclaim Time for What You Love",
    description:
      "Save 1+ hour daily with AI email responses that match your tone. Let AI handle 80% of routine emails while you focus on creativity, relationships, and meaningful work.",
    metric: "80% time saved",
  },
  {
    icon: Calendar,
    title: "Strengthen Human Connections",
    description:
      "End scheduling back-and-forth forever. AI finds optimal meeting times instantly, giving you more time for genuine human interaction and relationship building.",
    metric: "5x faster scheduling",
  },
  {
    icon: CheckCircle,
    title: "Amplify Your Human Potential",
    description:
      "AI prioritizes your to-do list based on deadlines and importance, freeing your mind for strategic thinking, creative problem-solving, and meaningful decisions.",
    metric: "40% more productive",
  },
  {
    icon: Shield,
    title: "Your Values, Protected",
    description:
      "Built with respect for human autonomy and privacy. Your data stays yours with strict isolation—no sharing, no selling. AI serves your values, not corporate interests.",
    metric: "100% data ownership",
  },
  {
    icon: Zap,
    title: "Freedom to Pursue Your Passions",
    description:
      "Automate repetitive tasks across all your apps so you can dive deeper into learning, growing, and pursuing activities that bring you fulfillment and purpose.",
    metric: "3+ hours saved daily",
  },
  {
    icon: Users,
    title: "Time Liberation, Not Just Efficiency",
    description:
      "Seamlessly coordinate with team members through AI-managed workflows, creating space for reflection, spontaneity, and the moments that make life meaningful.",
    metric: "50% faster teamwork",
  },
]

export default function FeaturesSection() {
  return (
    <section id="features" className="py-16 md:py-24 bg-slate-900 relative z-10">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            AI That Honors Your Humanity
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Technology should amplify what makes us human—our creativity, relationships, and capacity for meaningful work.
            Discover how AI can give you back the time to focus on what truly matters.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
          {features.map((feature) => (
            <div key={feature.title} className="bg-slate-800 p-6 rounded-lg shadow-lg text-center relative hover:bg-slate-750 transition-colors">
              <div className="flex justify-center mb-4">
                {React.createElement(feature.icon, { className: "h-12 w-12 text-brand-teal", "aria-hidden": "true" })}
              </div>
              {feature.metric && (
                <div className="absolute top-4 right-4">
                  <span className="bg-brand-teal text-slate-900 text-xs font-semibold px-2 py-1 rounded-full">
                    {feature.metric}
                  </span>
                </div>
              )}
              <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
              <p className="text-gray-300 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
